import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Search,
  MapPin,
  Filter,
  Award,
  Star,
  ChevronDown,
  Users,
  ArrowRight,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const regions = [
  "All Regions",
  "Georgetown",
  "Mahaica",
  "East Coast Demerara",
  "West Coast Demerara",
  "East Bank Demerara",
  "West Bank Demerara",
  "Berbice",
  "Essequibo",
  "Linden",
  "Bartica",
];

const serviceFilters = [
  "All Services",
  "Facials",
  "Nails",
  "Lash Extensions",
  "Body Waxing",
  "Hair Services",
  "Massage",
  "Brow Services",
  "Wig Services",
];

const courseFilters = [
  "All Courses",
  "Facial Training",
  "Manicure & Pedicure",
  "Acrylic Nails",
  "Lash Extensions",
  "Lash Lift",
  "Body Waxing",
  "Hair Services",
  "Advanced Hair",
  "Brow Training",
  "Wig Master Class",
];

const placeholderPhoto = "https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?auto=compress&cs=tinysrgb&w=400";

const AlumniCard = ({ alumni }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bento-card overflow-hidden group"
    data-testid={`alumni-card-${alumni.id}`}
  >
    <div className="h-48 overflow-hidden relative">
      <img
        src={alumni.photo_url || placeholderPhoto}
        alt={alumni.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      {alumni.featured && (
        <div className="absolute top-3 left-3 bg-[#C5A059] text-[#050505] text-xs font-semibold px-3 py-1 flex items-center gap-1">
          <Star size={10} fill="currentColor" /> Featured
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#121212] to-transparent h-16" />
    </div>

    <div className="p-5">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-heading text-lg text-white group-hover:text-[#C5A059] transition-colors">
            {alumni.name}
          </h3>
          {alumni.business_name && (
            <p className="font-body text-xs text-[#C5A059]">{alumni.business_name}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 text-[#A3A3A3] text-xs mb-3">
        <MapPin size={12} className="text-[#C5A059]" />
        <span>{alumni.location}, {alumni.region}</span>
      </div>

      <div className="flex flex-wrap gap-1 mb-3">
        {alumni.services_offered.slice(0, 3).map((service) => (
          <span
            key={service}
            className="bg-[#C5A059]/10 text-[#C5A059] text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5"
          >
            {service}
          </span>
        ))}
        {alumni.services_offered.length > 3 && (
          <span className="text-[#A3A3A3] text-[10px] px-2 py-0.5">
            +{alumni.services_offered.length - 3} more
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 text-[10px] text-[#A3A3A3] mb-4">
        <Award size={10} className="text-[#C5A059]" />
        <span>Trained: {alumni.courses_completed.join(", ")}</span>
      </div>

      {alumni.bio && (
        <p className="font-body text-xs text-[#A3A3A3] mb-4 line-clamp-2">{alumni.bio}</p>
      )}

      <Link
        to={`/alumni/${alumni.id}`}
        data-testid={`alumni-view-${alumni.id}`}
        className="w-full flex items-center justify-center gap-2 border border-[#C5A059] text-[#C5A059] font-body text-xs tracking-wider uppercase py-2.5 hover:bg-[#C5A059]/10 transition-all duration-300"
      >
        View Profile & Book <ArrowRight size={12} />
      </Link>
    </div>
  </motion.div>
);

export const AlumniDirectoryPage = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [regionFilter, setRegionFilter] = useState("All Regions");
  const [serviceFilter, setServiceFilter] = useState("All Services");
  const [courseFilter, setCourseFilter] = useState("All Courses");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAlumni();
  }, []);

  const fetchAlumni = async () => {
    try {
      const res = await axios.get(`${API}/alumni`);
      setAlumni(res.data);
    } catch (err) {
      console.error("Error fetching alumni:", err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return alumni.filter((a) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match =
          a.name.toLowerCase().includes(q) ||
          a.location.toLowerCase().includes(q) ||
          a.region.toLowerCase().includes(q) ||
          (a.business_name || "").toLowerCase().includes(q) ||
          a.services_offered.some((s) => s.toLowerCase().includes(q));
        if (!match) return false;
      }
      if (regionFilter !== "All Regions" && !a.region.toLowerCase().includes(regionFilter.toLowerCase())) return false;
      if (serviceFilter !== "All Services" && !a.services_offered.some((s) => s.toLowerCase().includes(serviceFilter.toLowerCase()))) return false;
      if (courseFilter !== "All Courses" && !a.courses_completed.some((c) => c.toLowerCase().includes(courseFilter.toLowerCase()))) return false;
      return true;
    });
  }, [alumni, searchQuery, regionFilter, serviceFilter, courseFilter]);

  return (
    <div data-testid="alumni-directory-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Luxury Beauty Certified
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Alumni<br />
              <span className="gold-gradient">Directory</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] max-w-2xl mx-auto leading-relaxed">
              Find Luxury Beauty trained professionals across Guyana.
              All alumni have completed our certified training programs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-6 bg-[#0A0A0A] border-b border-[#262626] sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, location, or service..."
                data-testid="alumni-search"
                className="w-full luxury-input pl-11"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              data-testid="alumni-filter-toggle"
              className={`flex items-center gap-2 px-4 py-3 border text-sm transition-all ${
                showFilters ? "border-[#C5A059] text-[#C5A059]" : "border-[#262626] text-[#A3A3A3] hover:border-[#C5A059]"
              }`}
            >
              <Filter size={14} />
              Filters
              <ChevronDown size={14} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>

          {showFilters && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Region</label>
                  <select
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                    data-testid="alumni-filter-region"
                    className="w-full luxury-input"
                  >
                    {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Service</label>
                  <select
                    value={serviceFilter}
                    onChange={(e) => setServiceFilter(e.target.value)}
                    data-testid="alumni-filter-service"
                    className="w-full luxury-input"
                  >
                    {serviceFilters.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Course Completed</label>
                  <select
                    value={courseFilter}
                    onChange={(e) => setCourseFilter(e.target.value)}
                    data-testid="alumni-filter-course"
                    className="w-full luxury-input"
                  >
                    {courseFilters.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="py-12 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-8">
            <p className="font-body text-sm text-[#A3A3A3]">
              {loading ? "Loading..." : `${filtered.length} professional${filtered.length !== 1 ? "s" : ""} found`}
            </p>
          </div>

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-[#262626] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">No professionals found</h3>
              <p className="font-body text-sm text-[#A3A3A3] mb-6">
                {alumni.length === 0
                  ? "Our alumni directory is growing. Check back soon!"
                  : "Try adjusting your search or filters."}
              </p>
              {alumni.length === 0 && (
                <Link
                  to="/alumni/apply"
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-6 py-3"
                >
                  Are you an alumni? Join the directory
                </Link>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <AlumniCard key={a.id} alumni={a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA for alumni to join */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bento-card p-10 md:p-16 text-center">
            <Award className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl text-white mb-4">
              Are You a Luxury Beauty Graduate?
            </h2>
            <p className="font-body text-[#A3A3A3] max-w-xl mx-auto mb-8">
              Join our alumni directory and get clients referred to you through our website.
              Expand your reach across Guyana.
            </p>
            <Link
              to="/alumni/apply"
              data-testid="alumni-join-cta"
              className="inline-flex items-center gap-2 bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all"
            >
              Apply to Join <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AlumniDirectoryPage;
